import {
  FileText,
  BarChart,
  Briefcase,
  Calendar,
  CheckSquare,
  LayoutDashboard,
  ListChecks,
  Mail,
  MessageSquare,
  Presentation,
  Camera,
  Shield,
  Handshake,
  Receipt,
  AlertTriangle
} from "lucide-react";

export interface EnhancedDocumentType {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  estimatedTime: string;
  difficulty: string;
  tags: string[];
  benefits: string[];
  useCases: string[];
  sections?: Array<{
    title: string;
    field: string;
  }>;
}

export const enhancedDocumentTypes: EnhancedDocumentType[] = [
  {
    id: "business_plan",
    title: "Business Plan",
    description: "Comprehensive roadmap for business success",
    icon: "📋",
    category: "Strategic Planning",
    estimatedTime: "60-90 minutes",
    difficulty: "Advanced",
    tags: ["strategy", "planning", "growth", "investment"],
    benefits: [
      "Attract investors",
      "Secure funding",
      "Guide business decisions",
      "Set strategic goals",
    ],
    useCases: [
      "Startups",
      "Small businesses",
      "Expansion plans",
      "Seeking investment",
    ],
    sections: [
      { title: "Company Overview", field: "companyOverview" },
      { title: "Market Analysis", field: "marketAnalysis" },
      { title: "Challenges", field: "challenges" },
      { title: "Objectives", field: "objectives" },
      { title: "Financial Projections", field: "financialProjections" },
    ],
  },
  {
    id: "financial_forecast",
    title: "Financial Forecast",
    description: "Predict future financial performance",
    icon: "📊",
    category: "Financial Planning",
    estimatedTime: "45-60 minutes",
    difficulty: "Intermediate",
    tags: ["finance", "forecast", "budgeting", "analysis"],
    benefits: [
      "Budgeting",
      "Financial health",
      "Investment decisions",
      "Performance tracking",
    ],
    useCases: [
      "Budgeting",
      "Financial planning",
      "Investment decisions",
      "Performance tracking",
    ],
    sections: [
      { title: "Revenue Projections", field: "revenueProjections" },
      { title: "Expense Forecasts", field: "expenseForecasts" },
      { title: "Cash Flow Analysis", field: "cashFlowAnalysis" },
    ],
  },
  {
    id: "strategic_plan",
    title: "Strategic Plan",
    description: "Long-term strategic planning document",
    icon: "🎯",
    category: "Strategic Planning",
    estimatedTime: "90-120 minutes",
    difficulty: "Advanced",
    tags: ["strategy", "planning", "goals", "vision"],
    benefits: [
      "Clear direction",
      "Goal alignment",
      "Resource optimization",
      "Performance measurement",
    ],
    useCases: [
      "Annual planning",
      "Business transformation",
      "Growth strategy",
      "Organizational alignment",
    ],
    sections: [
      { title: "Vision Statement", field: "visionStatement" },
      { title: "Current Situation", field: "currentSituation" },
      { title: "Strategic Objectives", field: "strategicObjectives" },
    ],
  },
  {
    id: "org_chart",
    title: "Organization Chart",
    description: "Visualize company structure and hierarchy",
    icon: "🏢",
    category: "HR & Management",
    estimatedTime: "20-30 minutes",
    difficulty: "Easy",
    tags: ["organization", "chart", "structure", "hierarchy"],
    benefits: [
      "Company structure",
      "Employee hierarchy",
      "Department assignments",
      "Reporting relationships",
    ],
    useCases: [
      "Company structure", 
      "Employee hierarchy",
      "Department assignments",
      "Reporting relationships",
    ],
    sections: [
      { title: "Company Overview", field: "companyOverview" },
      { title: "Department Structure", field: "departmentStructure" },
      { title: "Reporting Lines", field: "reportingLines" },
    ],
  },
  {
    id: "board_strategy",
    title: "Board Strategy",
    description: "Strategic presentation for board meetings",
    icon: "👥",
    category: "Executive",
    estimatedTime: "60-90 minutes",
    difficulty: "Advanced",
    tags: ["board", "strategy", "presentation", "executive"],
    benefits: [
      "Executive alignment",
      "Strategic clarity",
      "Board engagement",
      "Decision support",
    ],
    useCases: [
      "Board meetings",
      "Strategic reviews",
      "Executive presentations",
      "Investment decisions",
    ],
    sections: [
      { title: "Executive Summary", field: "executiveSummary" },
      { title: "Strategic Initiatives", field: "strategicInitiatives" },
      { title: "Financial Performance", field: "financialPerformance" },
    ],
  },
  {
    id: "budget_template",
    title: "Budget Template",
    description: "Comprehensive budget planning template",
    icon: "💰",
    category: "Financial",
    estimatedTime: "45-60 minutes",
    difficulty: "Intermediate",
    tags: ["budget", "finance", "planning", "costs"],
    benefits: [
      "Cost control",
      "Financial planning",
      "Resource allocation",
      "Performance tracking",
    ],
    useCases: [
      "Annual budgeting",
      "Department budgets",
      "Project budgets",
      "Cost management",
    ],
    sections: [
      { title: "Budget Summary", field: "budgetSummary" },
      { title: "Departmental Budgets", field: "departmentalBudgets" },
      { title: "Variance Analysis", field: "varianceAnalysis" },
    ],
  },
  {
    id: "tech_architecture",
    title: "Technology Architecture",
    description: "Technical system architecture documentation",
    icon: "⚙️",
    category: "Technology",
    estimatedTime: "90-120 minutes",
    difficulty: "Advanced",
    tags: ["technology", "architecture", "systems", "infrastructure"],
    benefits: [
      "System clarity",
      "Technical planning",
      "Infrastructure design",
      "Scalability planning",
    ],
    useCases: [
      "System design",
      "Infrastructure planning",
      "Technical documentation",
      "Architecture reviews",
    ],
    sections: [
      { title: "System Overview", field: "systemOverview" },
      { title: "Architecture Components", field: "architectureComponents" },
      { title: "Scalability Plan", field: "scalabilityPlan" },
    ],
  },
  {
    id: "digital_transformation_plan",
    title: "Digital Transformation Plan",
    description: "Comprehensive digital transformation strategy",
    icon: "🚀",
    category: "Technology",
    estimatedTime: "120-180 minutes",
    difficulty: "Advanced",
    tags: ["digital", "transformation", "strategy", "technology"],
    benefits: [
      "Digital readiness",
      "Process optimization",
      "Technology adoption",
      "Competitive advantage",
    ],
    useCases: [
      "Digital transformation",
      "Process modernization",
      "Technology adoption",
      "Business evolution",
    ],
    sections: [
      { title: "Current State Assessment", field: "currentStateAssessment" },
      { title: "Future State Vision", field: "futureStateVision" },
      { title: "Transformation Roadmap", field: "transformationRoadmap" },
    ],
  },
  {
    id: "process_optimization",
    title: "Process Optimization",
    description: "Analyze and optimize business processes",
    icon: "🔄",
    category: "Operations",
    estimatedTime: "60-90 minutes",
    difficulty: "Intermediate",
    tags: ["process", "optimization", "efficiency", "operations"],
    benefits: [
      "Improved efficiency",
      "Cost reduction",
      "Quality improvement",
      "Resource optimization",
    ],
    useCases: [
      "Process improvement",
      "Operational efficiency",
      "Cost optimization",
      "Quality enhancement",
    ],
    sections: [
      { title: "Current Process Analysis", field: "currentProcessAnalysis" },
      { title: "Inefficiency Identification", field: "inefficiencyIdentification" },
      { title: "Optimization Plan", field: "optimizationPlan" },
    ],
  },
  {
    id: "operations_manual",
    title: "Operations Manual",
    description: "Comprehensive operational procedures guide",
    icon: "📖",
    category: "Operations",
    estimatedTime: "120-180 minutes",
    difficulty: "Advanced",
    tags: ["operations", "manual", "procedures", "guide"],
    benefits: [
      "Standardized processes",
      "Training resource",
      "Operational consistency",
      "Knowledge preservation",
    ],
    useCases: [
      "Process documentation",
      "Training materials",
      "Operational standards",
      "Quality assurance",
    ],
    sections: [
      { title: "Operational Overview", field: "operationalOverview" },
      { title: "Departmental Procedures", field: "departmentalProcedures" },
      { title: "Quality Standards", field: "qualityStandards" },
    ],
  },
  {
    id: "product_roadmap",
    title: "Product Roadmap",
    description: "Strategic product development timeline",
    icon: "🗺️",
    category: "Product",
    estimatedTime: "90-120 minutes",
    difficulty: "Advanced",
    tags: ["product", "roadmap", "development", "strategy"],
    benefits: [
      "Product strategy",
      "Development planning",
      "Stakeholder alignment",
      "Resource planning",
    ],
    useCases: [
      "Product planning",
      "Development strategy",
      "Feature prioritization",
      "Release planning",
    ],
    sections: [
      { title: "Product Vision", field: "productVision" },
      { title: "Feature Priorities", field: "featurePriorities" },
      { title: "Resource Requirements", field: "resourceRequirements" },
    ],
  },
  {
    id: "user_research_plan",
    title: "User Research Plan",
    description: "Comprehensive user research and analysis plan",
    icon: "🔍",
    category: "Product",
    estimatedTime: "60-90 minutes",
    difficulty: "Intermediate",
    tags: ["user", "research", "analysis", "insights"],
    benefits: [
      "User insights",
      "Product validation",
      "Market understanding",
      "Design guidance",
    ],
    useCases: [
      "Product research",
      "User validation",
      "Market analysis",
      "Design research",
    ],
    sections: [
      { title: "Research Objectives", field: "researchObjectives" },
      { title: "Target Audience", field: "targetAudience" },
      { title: "Research Methods", field: "researchMethods" },
    ],
  },
  {
    id: 'consulting_agreement',
    title: 'Consulting Agreement',
    description: 'Professional services consulting contract with scope, deliverables, and payment terms',
    icon: '📄',
    category: 'Contracts',
    estimatedTime: '45-60 minutes',
    difficulty: 'Advanced',
    tags: ['contract', 'consulting', 'legal', 'services'],
    benefits: ['Legal protection', 'Clear expectations', 'Payment security', 'Professional standards'],
    useCases: ['Independent consulting', 'Business advisory', 'Technical consulting', 'Strategic planning'],
    sections: [
      { title: 'Consultant Information', field: 'consultantInfo' },
      { title: 'Client Information', field: 'clientInfo' },
      { title: 'Services Description', field: 'servicesDescription' },
      { title: 'Deliverables', field: 'deliverables' },
      { title: 'Timeline & Milestones', field: 'timeline' },
      { title: 'Compensation Structure', field: 'compensation' },
      { title: 'Confidentiality', field: 'confidentiality' }
    ]
  },
  {
    id: 'media_release',
    title: 'Media Release Form',
    description: 'Legal document for media usage rights and image/video permissions',
    icon: '📷',
    category: 'Contracts',
    estimatedTime: '20-30 minutes',
    difficulty: 'Intermediate',
    tags: ['media', 'release', 'legal', 'photography'],
    benefits: ['Legal compliance', 'Usage rights clarity', 'Professional documentation', 'Risk mitigation'],
    useCases: ['Photography shoots', 'Video production', 'Marketing content', 'Event documentation'],
    sections: [
      { title: 'Release Information', field: 'releaseInfo' },
      { title: 'Subject Information', field: 'subjectInfo' },
      { title: 'Media Usage Rights', field: 'usageRights' },
      { title: 'Compensation', field: 'compensation' },
      { title: 'Release Terms', field: 'releaseTerms' }
    ]
  },
  {
    id: 'nda_agreement',
    title: 'Non-Disclosure Agreement',
    description: 'Confidentiality agreement to protect sensitive business information',
    icon: '🛡️',
    category: 'Contracts',
    estimatedTime: '30-40 minutes',
    difficulty: 'Advanced',
    tags: ['nda', 'confidentiality', 'legal', 'protection'],
    benefits: ['Information protection', 'Legal security', 'Business confidentiality', 'Trust building'],
    useCases: ['Business partnerships', 'Employee agreements', 'Investor meetings', 'Vendor relationships'],
    sections: [
      { title: 'Agreement Parties', field: 'parties' },
      { title: 'Confidential Information Definition', field: 'confidentialInfo' },
      { title: 'Obligations', field: 'obligations' },
      { title: 'Exceptions', field: 'exceptions' },
      { title: 'Duration', field: 'duration' },
      { title: 'Remedies', field: 'remedies' }
    ]
  },
  {
    id: 'service_contract',
    title: 'Service Contract',
    description: 'General service agreement for business-to-business or business-to-client relationships',
    icon: '🤝',
    category: 'Contracts',
    estimatedTime: '40-50 minutes',
    difficulty: 'Advanced',
    tags: ['contract', 'services', 'legal', 'business'],
    benefits: ['Clear service terms', 'Payment protection', 'Legal framework', 'Professional standards'],
    useCases: ['Service providers', 'Maintenance contracts', 'Support agreements', 'Professional services'],
    sections: [
      { title: 'Contract Overview', field: 'overview' },
      { title: 'Parties Involved', field: 'parties' },
      { title: 'Terms and Conditions', field: 'terms' },
      { title: 'Payment Terms', field: 'paymentTerms' },
      { title: 'Scope of Work', field: 'scopeOfWork' },
      { title: 'Termination Clause', field: 'termination' },
      { title: 'Legal Provisions', field: 'legalProvisions' }
    ]
  },
  {
    id: 'risk_mitigation_plan',
    title: 'Risk Mitigation Plan',
    description: 'Comprehensive risk assessment and mitigation strategy',
    icon: '⚠️',
    category: 'Risk Management',
    estimatedTime: '90-120 minutes',
    difficulty: 'Advanced',
    tags: ['risk', 'mitigation', 'planning', 'assessment'],
    benefits: ['Risk identification', 'Mitigation strategies', 'Business continuity', 'Stakeholder confidence'],
    useCases: ['Project planning', 'Business operations', 'Compliance requirements', 'Strategic planning'],
    sections: [
      { title: 'Executive Summary', field: 'executiveSummary' },
      { title: 'Risk Register', field: 'risks' },
      { title: 'Monitoring Plan', field: 'monitoringPlan' }
    ]
  },
  {
    id: 'invoice_generator',
    title: 'Invoice Generator',
    description: 'Professional invoice creation with custom branding',
    icon: '🧾',
    category: 'Finance',
    estimatedTime: '30-45 minutes',
    difficulty: 'Easy',
    tags: ['invoice', 'billing', 'finance', 'payment'],
    benefits: ['Professional invoicing', 'Payment tracking', 'Brand consistency', 'Time savings'],
    useCases: ['Freelancing', 'Small business', 'Service providers', 'Consulting'],
    sections: [
      { title: 'Company Information', field: 'companyInfo' },
      { title: 'Client Details', field: 'clientDetails' },
      { title: 'Invoice Items', field: 'items' },
      { title: 'Payment Terms', field: 'paymentTerms' }
    ]
  },
  {
    id: 'proposal_generator',
    title: 'Proposal Generator',
    description: 'Professional project proposals with custom letterheads',
    icon: '📄',
    category: 'Business Development',
    estimatedTime: '60-90 minutes',
    difficulty: 'Intermediate',
    tags: ['proposal', 'business', 'projects', 'client'],
    benefits: ['Professional proposals', 'Win more clients', 'Consistent branding', 'Structured approach'],
    useCases: ['Consulting projects', 'Service proposals', 'Project bids', 'Client presentations'],
    sections: [
      { title: 'Executive Summary', field: 'executiveSummary' },
      { title: 'Project Overview', field: 'projectOverview' },
      { title: 'Methodology', field: 'methodology' },
      { title: 'Pricing', field: 'pricing' },
      { title: 'Next Steps', field: 'nextSteps' }
    ]
  }
];
